import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Filter } from 'lucide-react';
import customFieldsService from '../services/customFieldsService';
import { useFacility } from '../context/FacilityContext';
import { useAuth } from '../context/AuthContext';

const CustomFieldsList = () => {
  const { currentFacility } = useFacility();
  const facilityId = currentFacility?._id || currentFacility?.id || '';
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const { isManager } = useAuth();
  const navigate = useNavigate();

  // Re-fetch when the facility changes — definitions are per-facility,
  // so a stale list would show another site's fields.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchFields(); }, [facilityId]);

  const fetchFields = async () => {
    try {
      setLoading(true);
      const { data } = await customFieldsService.getAll(facilityId);
      setFields(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching custom fields:', err);
      setError('Failed to load custom fields. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, label) => {
    if (!window.confirm(`Deactivate "${label}"? This hides it from forms but preserves existing data.`)) return;
    try {
      await customFieldsService.remove(facilityId, id);
      fetchFields();
    } catch (err) {
      console.error('Error deleting field:', err);
      alert('Failed to deactivate field.');
    }
  };

  const filteredFields = fields.filter(field => {
    if (filter === 'active') return field.is_active;
    if (filter === 'inactive') return !field.is_active;
    return true;
  });

  if (!isManager()) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-[13px] text-red-500">You do not have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Custom Fields</h1>
          <p className="text-[13px] text-slate-400 mt-0.5">Manage dynamic fields for customer profiles</p>
        </div>
        <Link to="/settings/custom-fields/new" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors">
          <Plus className="h-3.5 w-3.5" /> Create New Field
        </Link>
      </div>

      <div className="rounded-xl border border-slate-200/80 bg-white p-3 mb-4 flex items-center gap-2">
        <Filter className="h-3.5 w-3.5 text-slate-400" />
        <span className="text-[13px] font-medium text-slate-600">Status:</span>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-[13px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500">
          <option value="all">All Fields</option>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive Only</option>
        </select>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-red-200/80 bg-red-50 p-3">
          <p className="text-[13px] text-red-600">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100">
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Label / ID</th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Type</th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Category</th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Config</th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                <th className="text-right px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredFields.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-8 text-center text-[13px] text-slate-400">No custom fields found.</td></tr>
              ) : (
                filteredFields.map((field) => (
                  <tr key={field.field_id} className={`hover:bg-slate-50/50 transition-colors ${!field.is_active ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="text-[13px] font-medium text-slate-700">{field.label}</div>
                      <div className="text-[11px] text-slate-400 font-mono">{field.field_id}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-50 text-blue-600">{field.field_type}</span>
                    </td>
                    <td className="px-4 py-3 text-[13px] text-slate-500">{field.category || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5">
                        {field.is_required && (
                          <span className="text-[11px] text-red-500 flex items-center gap-1">
                            <span className="h-1 w-1 bg-red-500 rounded-full"></span>Required
                          </span>
                        )}
                        {field.options && field.options.length > 0 && (
                          <span className="text-[11px] text-slate-400">{field.options.length} option{field.options.length !== 1 ? 's' : ''}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${field.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-50 text-slate-500'}`}>
                        {field.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => navigate(`/settings/custom-fields/${field.field_id}`)} className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                          <Edit className="h-3.5 w-3.5" />
                        </button>
                        {field.is_active && (
                          <button onClick={() => handleDelete(field.field_id, field.label)} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomFieldsList;
